import { useEffect, useRef, useState } from "react";
import { onlineManager } from "@tanstack/react-query";
import { Icon } from "../ui/Icon";

/**
 * The connection state, said out loud — the difference between an app that
 * "isn't working" and one that is plainly offline and still usable.
 *
 * Everything the reader has already opened is on disk (the persisted query
 * cache in `lib/persist.ts`, the precached shell and chant JSON in the service
 * worker), so losing signal is not an error state: the library, the documents
 * and the chants keep working. Without a word on screen that reads as breakage,
 * which is what makes an offline-capable app feel broken rather than resilient.
 *
 * Three states, and the third is the one usually left out:
 *
 *   online          nothing is shown
 *   offline         a calm persistent bar — "Offline · showing what you have"
 *   just-reconnected a brief "Back online" that withdraws itself
 *
 * The last one matters because react-query refetches on reconnect: the screen
 * is about to change under the reader, and this says why.
 *
 * `onlineManager` rather than `navigator.onLine` alone: react-query already
 * owns this signal, it is what drives `refetchOnReconnect`, and taking it from
 * the same place means the banner cannot disagree with the data.
 */
export function ConnectionBanner() {
  const [online, setOnline] = useState(() => onlineManager.isOnline());
  const [justBack, setJustBack] = useState(false);
  const wasOffline = useRef(!onlineManager.isOnline());

  useEffect(() => {
    return onlineManager.subscribe((next) => {
      setOnline(next);
      if (!next) {
        wasOffline.current = true;
        setJustBack(false);
        return;
      }
      // Only announce a RECOVERY, never the ordinary online state at boot.
      if (wasOffline.current) {
        wasOffline.current = false;
        setJustBack(true);
      }
    });
  }, []);

  useEffect(() => {
    if (!justBack) return;
    const t = window.setTimeout(() => setJustBack(false), 2600);
    return () => window.clearTimeout(t);
  }, [justBack]);

  if (online && !justBack) return null;

  const offline = !online;
  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-x-0 z-[60] flex justify-center px-4 pointer-events-none"
      // Sits directly under the fixed header, clear of the notch on a phone.
      style={{ top: "calc(env(safe-area-inset-top, 0px) + 3.25rem)" }}
    >
      <div
        className="pointer-events-auto inline-flex items-center gap-2 rounded-full px-4 py-1.5 font-grotesk text-[0.7rem] tracking-[0.14em] uppercase shadow-sm transition-opacity duration-500"
        style={{
          backgroundColor: offline
            ? "color-mix(in srgb, var(--color-ink) 88%, transparent)"
            : "color-mix(in srgb, var(--color-violet) 90%, transparent)",
          color: "var(--color-vellum)",
          border: "1px solid color-mix(in srgb, var(--color-ink) 30%, transparent)",
        }}
      >
        <Icon name={offline ? "sliders" : "check"} size="0.95em" />
        {offline ? "Offline · showing what you have" : "Back online"}
      </div>
    </div>
  );
}
